import { DEFAULT_VISIBLE_LAYERS } from '@civilgrid/utils/src/layerConfig/defaultLayers';
import { proxy } from 'valtio';
import { proxySet } from 'valtio/utils';
import type { AvailableLayer } from '~/api/availableLayers';
import { getOrgLayerConfig, type OrgLayerConfigDocument } from '~/api/orgLayerConfig';
import { computeLibraryLayerIds } from './computeLibraryLayerIds';
import { documentToState, type OrgLayerConfigState, stateToDocument } from './orgLayerConfig.mapping';
import { createSupersessionGuard } from './supersessionGuard';

export type OrgLayerConfigLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

/** Non-edited document fields retained verbatim for a lossless write-back. */
interface OrgLayerConfigMeta {
  /** Every document leaf id, in document order. */
  nodeIds: string[];
  /** id → server-written GC stamp, serialized back untouched. */
  missingSince: Map<string, string>;
}

/**
 * Org-admin default-layer config store — a membership-only sibling of `LayerConfigStore`.
 * No `visible`/`pinned`, no map-store mirroring, no localStorage seed: the org document
 * just records which leaf ids are the org's default set. Multi-admin, last-write-wins;
 * the page re-bootstraps on open (refetch-on-open) so an admin starts from the latest save.
 */
export class OrgLayerConfigStore {
  loadState: OrgLayerConfigLoadState = 'loading';
  /** Armed ONLY after a successful hydrate — the no-overwrite invariant's decision point. */
  canPersist = false;
  /** Set by the sync when a PUT fails and cleared on the next success; drives the save-error banner. */
  saveError = false;
  /** Non-edited document fields retained for a lossless write-back; `null` until first hydrate. */
  meta: OrgLayerConfigMeta | null = null;
  /** Catalog **group** ids "in the org default set" — the any-present projection the page renders. */
  libraryLayerIds: Set<string> = proxySet<string>();
  /** Bumped whenever the catalog changes — a cheap reactive dep for the page. */
  catalogVersion = 0;

  // The org the latest bootstrap is resolving for; a response for any other org is stale.
  private activeOrganizationId: string | null = null;
  // Monotonic per-bootstrap token; a resume after an await proceeds only while still latest.
  private readonly bootstrapGuard = createSupersessionGuard();
  // Lifecycle callback slots the sync controller registers into (store never imports the sync).
  private hydrateListeners = new Set<(document: OrgLayerConfigDocument, organizationId: string | null) => void>();
  // Group-grain catalog rows keyed by group node id; `null` until the catalog arrives.
  private catalog: Map<string, AvailableLayer> | null = null;
  // The org `catalog` was loaded for; `null` is org-agnostic (matches any org).
  private catalogOrganizationId: string | null = null;

  /** Called by the page once `useAvailableLayers` resolves. Order-independent with hydrate. */
  setCatalog(layers: readonly AvailableLayer[], organizationId: string | null = null) {
    this.catalog = new Map(layers.map((layer) => [layer.key, layer]));
    this.catalogOrganizationId = organizationId;
    this.catalogVersion += 1;
    this.rebuildLibraryLayerIds();
  }

  /**
   * The catalog, but only if it matches the active org — a stale previous-org catalog (still
   * resolving after an org switch) is treated as ABSENT rather than used to compute leaf ids
   * for the wrong org. Org-agnostic (`null`) catalogs match any org.
   */
  private activeCatalog(): Map<string, AvailableLayer> | null {
    if (!this.catalog) return null;
    if (this.catalogOrganizationId !== null && this.catalogOrganizationId !== this.activeOrganizationId) return null;
    return this.catalog;
  }

  /**
   * Load the org document. Both `found` and `not_seeded` carry a usable document (the latter =
   * computed CivilGrid defaults) and are hydrated identically; `error` fails closed.
   */
  async bootstrap(organizationId: string) {
    const token = this.bootstrapGuard.next();
    this.activeOrganizationId = organizationId;
    this.loadState = 'loading';
    this.canPersist = false;
    // Clear the previous org's selection immediately so a newly-arriving catalog can't be projected
    // through stale membership before this org hydrates (avoids a brief cross-org selection flash).
    this.meta = null;
    this.rebuildLibraryLayerIds();

    const result = await getOrgLayerConfig(organizationId);
    if (!this.bootstrapGuard.isCurrent(token)) return; // superseded

    if (result.status === 'error') {
      this.markFailed();
      return;
    }
    try {
      this.hydrate(result.document);
    } catch {
      this.markFailed();
    }
  }

  hydrate(document: OrgLayerConfigDocument) {
    const state = documentToState(document);
    this.meta = { nodeIds: state.nodeIds, missingSince: state.missingSince };
    this.rebuildLibraryLayerIds();
    this.loadState = 'loaded';
    this.canPersist = true;
    this.saveError = false;
    // Fire AFTER persistence is armed so the sync baselines against exactly what was hydrated.
    const canonical = this.toDocument();
    this.hydrateListeners.forEach((listener) => {
      listener(canonical, this.activeOrganizationId);
    });
  }

  markFailed() {
    this.loadState = 'failed';
    this.canPersist = false;
  }

  /** Add every leaf of the bucket to the org default set (bucket grain). */
  addToLibrary(groupNodeId: string) {
    const layer = this.activeCatalog()?.get(groupNodeId);
    if (!layer || !this.meta) return;
    const present = new Set(this.meta.nodeIds);
    const missing = layer.subLayers.map((subLayer) => subLayer.key).filter((id) => !present.has(id));
    if (missing.length === 0) return;
    for (const id of missing) this.meta.nodeIds.push(id);
    this.rebuildLibraryLayerIds();
  }

  /** Remove every leaf of the bucket from the org default set (bucket grain). */
  removeFromLibrary(groupNodeId: string) {
    const layer = this.activeCatalog()?.get(groupNodeId);
    if (!layer || !this.meta) return;
    const removeIds = new Set(layer.subLayers.map((subLayer) => subLayer.key));
    const next = this.meta.nodeIds.filter((id) => !removeIds.has(id));
    if (next.length === this.meta.nodeIds.length) return;
    this.meta.nodeIds = next;
    removeIds.forEach((id) => {
      this.meta?.missingSince.delete(id);
    });
    this.rebuildLibraryLayerIds();
  }

  /**
   * Reset the org default set to EXACTLY the CivilGrid defaults (leaf grain) — matches the
   * server's `buildOrgSeedConfig`. The debounced sync persists it like any edit.
   */
  resetToDefaultLayers() {
    if (!this.meta) return;
    this.meta = { nodeIds: [...DEFAULT_VISIBLE_LAYERS], missingSince: new Map() };
    this.rebuildLibraryLayerIds();
  }

  /** True iff the current membership is exactly `DEFAULT_VISIBLE_LAYERS`. Disables Reset at defaults. */
  isAtDefaultLayers(): boolean {
    if (!this.meta) return false;
    const defaults = new Set(DEFAULT_VISIBLE_LAYERS);
    const members = new Set(this.meta.nodeIds);
    if (members.size !== defaults.size) return false;
    for (const id of defaults) if (!members.has(id)) return false;
    return true;
  }

  /** Current membership as a canonical document (membership-only). */
  toDocument(): OrgLayerConfigDocument {
    return stateToDocument(this.getState() ?? { nodeIds: [], missingSince: new Map() });
  }

  /** Current state slice, or `null` before the first hydrate. Read by the sync. */
  getState(): OrgLayerConfigState | null {
    return this.meta;
  }

  /** Register a hydrate listener (the sync controller uses this to baseline). Returns an unsubscribe. */
  onHydrate(listener: (document: OrgLayerConfigDocument, organizationId: string | null) => void): () => void {
    this.hydrateListeners.add(listener);
    return () => this.hydrateListeners.delete(listener);
  }

  /** Group is "in the org default set" iff ANY of its current catalog leaves is a document entry. */
  private rebuildLibraryLayerIds() {
    this.libraryLayerIds.clear();
    const catalog = this.activeCatalog();
    if (!this.meta || !catalog) return;
    for (const id of computeLibraryLayerIds(catalog, this.meta.nodeIds)) this.libraryLayerIds.add(id);
  }
}

const orgLayerConfigStore = proxy(new OrgLayerConfigStore());

/** Whether the page may be edited for a given load state (UX only; `canPersist` enforces the invariant). */
export function canEditOrgLayerConfig(loadState: OrgLayerConfigLoadState): boolean {
  return loadState === 'loaded';
}

export default orgLayerConfigStore;
